import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ReactNode } from 'react';

import BusinessIcon from '@/assets/icons/BusinessIcon';
import ClockIcon from '@/assets/icons/ClockIcon';
import { formatDuration } from '@/utils/helperFunctions';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import AgentDeliveryTimeIcon from '@/assets/icons/AgentDeliveryTimeIcon';

interface SummaryCardsProps {
  avgBusinessSetupTime: number;
  avgAgentDeliveryTime: number;
  avgGoLiveTime: number;
}

function formatAverageTime(seconds: number) {
  if (!seconds || seconds <= 0) return '0 hours';

  if (seconds >= 3600) {
    const hours = Math.round(seconds / 3600);
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }

  return formatDuration(seconds);
}

function MetricCard({
  title,
  tooltip,
  value,
  icon
}: {
  title: string;
  tooltip: string;
  value: string;
  icon: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        borderRadius: '12px',
        p: '16px',
        height: '100%'
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
        <Stack direction='row' alignItems='center' gap={0.5} flex={1} minWidth={0}>
          <Typography fontSize={14} color='text.secondary' fontWeight={500}>
            {title}
          </Typography>
          <Tooltip title={tooltip} arrow placement='top'>
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'text.secondary',
                cursor: 'help',
                flexShrink: 0,
                '& svg': { width: 16, height: 16 }
              }}
            >
              <AiOutlineInfoCircle />
            </Box>
          </Tooltip>
        </Stack>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.palette.primary.light,
            color: theme.palette.primary.main,
            flexShrink: 0,
            ml: 1,
            '& svg': { width: 18, height: 18 }
          }}
        >
          {icon}
        </Box>
      </Stack>

      <Typography mt={1.5} fontSize={20} fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}

export default function SummaryCards({ avgBusinessSetupTime, avgAgentDeliveryTime, avgGoLiveTime }: SummaryCardsProps) {
  const cards = [
    {
      key: 'setup',
      title: 'Average Business Setup Time',
      tooltip: 'Time between a business completing the agent info form and their account being created in the portal.',
      value: formatAverageTime(avgBusinessSetupTime),
      icon: <ClockIcon />
    },
    {
      key: 'delivery',
      title: 'Average Business Agent Delivery Time',
      tooltip: 'Time between the business being created in the portal and the agent being finalized.',
      value: formatAverageTime(avgAgentDeliveryTime),
      icon: <AgentDeliveryTimeIcon />
    },
    {
      key: 'goLive',
      title: 'Average Business Go-Live Time',
      tooltip: 'Time between the agent finalization and the agent will fully stabilized.',
      value: formatAverageTime(avgGoLiveTime),
      icon: <BusinessIcon size={18} />
    }
  ];

  return (
    <Grid container spacing={2} mb={3}>
      {cards.map((card) => (
        <Grid key={card.key} size={{ xs: 12, sm: 6, lg: 4 }}>
          <MetricCard title={card.title} tooltip={card.tooltip} value={card.value} icon={card.icon} />
        </Grid>
      ))}
    </Grid>
  );
}
